import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import PurchaseRequestsTable from '../purchaseRequests/PurchaseRequestsTable';
import UpsellingRequestItemsTable from '../commercialRequests/UpsellingRequestItemsTable';
import RequestListViewTabs from '../commercialRequests/RequestListViewTabs';
import { PurchaseRequestsForm } from '../forms/PurchaseRequestsForm';
import PurchaseRequestDetailModal from './PurchaseRequestDetailModal';
import purchaseRequestsAPI from '../../services/purchaseRequestsAPI';
import suppliersAPI from '../../services/suppliersAPI';
import usersAPI from '../../services/usersAPI';
import { useUIColors } from '../../hooks/useTheme';
import { useAuth } from '../../contexts/AuthContext';
import { purchaseRequestAreaOptions } from './purchaseRequestAreas';
import { buildUpsellingProductOptions, emptyUpsellingLine } from './upsellingCatalogUtils';
import API_BASE_URL from '../../config/api';
import { authFetch } from '../../services/apiClient';
import {
  isCommercialUpsellingRequest,
  notifyPurchaseRequestsChanged,
  PURCHASE_REQUESTS_CHANGED_EVENT,
} from './commercialRequestsUtils';
import {
  buildDefaultLineSelections,
  isTcoatQuotationItem,
  partitionQuotationLineItems,
  selectedQuotationLines,
  updateLineSelectionMap,
  validateLineSelections,
} from './quotationItemClassification';

const saleDetailLineItems = (detail) => {
  if (Array.isArray(detail?.items)) return detail.items;
  if (Array.isArray(detail?.quotation_items)) return detail.quotation_items;
  return [];
};

const upsellingLinesFromSaleDetail = (detail, paintSkuSet, upsellingSkuSet) => {
  const lineItems = saleDetailLineItems(detail);
  const { upselling } = partitionQuotationLineItems(lineItems, paintSkuSet, upsellingSkuSet);
  if (upselling.length) return upselling;
  const withoutTcoat = lineItems.filter(
    (item) => !isTcoatQuotationItem(item, paintSkuSet, upsellingSkuSet)
  );
  return withoutTcoat.length ? withoutTcoat : lineItems;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = {
  request_code: '',
  requester_name: '',
  department: 'Comercial',
  supplier_id: '',
  request_date: todayIso(),
  needed_by_date: '',
  priority: 'Normal',
  status: 'Enviada',
  justification: 'Upselling · ',
  notes: 'Solicitud upselling desde área comercial.',
  sale_id: '',
  sale_code: '',
  quotation_code: '',
  items: [emptyUpsellingLine()],
};

const UpsellingRequestsPanel = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [itemRows, setItemRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [error, setError] = useState('');
  const [itemsError, setItemsError] = useState('');
  const [listTab, setListTab] = useState('requests');
  const [detailRequest, setDetailRequest] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [suppliers, setSuppliers] = useState([]);
  const [users, setUsers] = useState([]);
  const [upsellingProducts, setUpsellingProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [paintSkuSet, setPaintSkuSet] = useState(() => new Set());
  const [upsellingSkuSet, setUpsellingSkuSet] = useState(() => new Set());
  const [saleLineItems, setSaleLineItems] = useState([]);
  const [selectionMap, setSelectionMap] = useState({});
  const [saleLinesLoading, setSaleLinesLoading] = useState(false);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await purchaseRequestsAPI.getAll();
      const rows = (Array.isArray(data) ? data : []).filter(isCommercialUpsellingRequest);
      setRequests(rows);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'No se pudieron cargar las solicitudes de upselling.');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadItems = useCallback(async () => {
    setItemsLoading(true);
    setItemsError('');
    try {
      const data = await purchaseRequestsAPI.getAllItems();
      const rows = (Array.isArray(data) ? data : []).filter(isCommercialUpsellingRequest);
      setItemRows(rows);
    } catch (err) {
      console.error(err);
      setItemsError(err?.message || 'No se pudieron cargar los ítems de upselling.');
      setItemRows([]);
    } finally {
      setItemsLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadRequests(), loadItems()]);
  }, [loadRequests, loadItems]);

  const loadLookups = useCallback(async () => {
    try {
      const [suppliersData, usersData, upsellingResponse, salesResponse] = await Promise.all([
        suppliersAPI.getAll(),
        usersAPI.getAll().catch((err) => {
          console.error('Error loading users for upselling:', err);
          return [];
        }),
        authFetch(`${API_BASE_URL}/api/quotations/upselling-products`, { cache: 'no-store' }).catch((err) => {
          console.error('Error loading upselling catalog:', err);
          return null;
        }),
        authFetch(`${API_BASE_URL}/api/quotations/sales`, { cache: 'no-store' }).catch((err) => {
          console.error('Error loading sales for upselling:', err);
          return null;
        }),
      ]);
      setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
      setUsers(Array.isArray(usersData) ? usersData : []);
      if (upsellingResponse?.ok) {
        const raw = await upsellingResponse.json();
        const list = Array.isArray(raw) ? raw : [];
        setUpsellingProducts(list);
        const set = new Set();
        list.forEach((row) => {
          [row.upselling_code, row.legacy_code, row.legacy_code2].forEach((code) => {
            const key = String(code || '').trim().toUpperCase();
            if (key) set.add(key);
          });
        });
        setUpsellingSkuSet(set);
      } else {
        setUpsellingProducts([]);
        setUpsellingSkuSet(new Set());
      }
      if (salesResponse?.ok) {
        const rawSales = await salesResponse.json();
        setSales(Array.isArray(rawSales) ? rawSales : []);
      } else {
        setSales([]);
      }
      const paintRes = await authFetch(`${API_BASE_URL}/api/quotations/products`, {
        cache: 'no-store',
      }).catch(() => null);
      if (paintRes?.ok) {
        const rawPaint = await paintRes.json();
        const set = new Set();
        (Array.isArray(rawPaint) ? rawPaint : []).forEach((row) => {
          const key = String(row.sku || '').trim().toUpperCase();
          if (key) set.add(key);
        });
        setPaintSkuSet(set);
      }
    } catch (err) {
      console.error('Error loading upselling lookups:', err);
    }
  }, []);

  useEffect(() => {
    refreshAll();
    loadLookups();
  }, [refreshAll, loadLookups]);

  useEffect(() => {
    const refresh = () => refreshAll();
    window.addEventListener(PURCHASE_REQUESTS_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(PURCHASE_REQUESTS_CHANGED_EVENT, refresh);
  }, [refreshAll]);

  const supplierOptions = useMemo(
    () =>
      suppliers.map((s) => ({
        value: String(s.id),
        label: `${s.supplier_code || ''} · ${s.name}`.trim(),
      })),
    [suppliers]
  );

  const areaOptions = useMemo(() => purchaseRequestAreaOptions(['Comercial']), []);

  const requesterOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    (users || []).forEach((row) => {
      const status = String(row?.status || 'Activo').trim().toLowerCase();
      if (status && status !== 'activo' && status !== 'active') return;
      const label = String(row?.full_name || row?.user_code || '').trim();
      if (!label) return;
      const key = label.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      options.push({ value: label, label });
    });
    const sessionName = String(user?.full_name || '').trim();
    if (sessionName && !seen.has(sessionName.toLowerCase())) {
      options.unshift({ value: sessionName, label: sessionName });
    }
    return options.sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [users, user]);

  const productOptions = useMemo(
    () => buildUpsellingProductOptions(upsellingProducts),
    [upsellingProducts]
  );

  const saleOptions = useMemo(() => {
    const seen = new Set();
    return (sales || [])
      .map((sale) => {
        const id = Number(sale?.id || sale?.sale_id || 0);
        if (!id || seen.has(id)) return null;
        seen.add(id);
        const code = String(sale?.sale_code || `V-${id}`).trim();
        const client = String(sale?.organization_name || 'Sin cliente').trim();
        const quote = String(sale?.quotation_code || '').trim();
        return {
          value: String(id),
          label: `${code}${quote ? ` · ${quote}` : ''} · ${client}`,
          sale_code: code,
          quotation_code: quote,
          sort_key: id,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const byCode = String(b.sale_code || '').localeCompare(String(a.sale_code || ''), 'es', {
          numeric: true,
          sensitivity: 'base',
        });
        return byCode !== 0 ? byCode : Number(b.sort_key || 0) - Number(a.sort_key || 0);
      });
  }, [sales]);

  const resetSaleLines = () => {
    setSaleLineItems([]);
    setSelectionMap({});
    setSaleLinesLoading(false);
  };

  const handleSaleChange = async (saleIdValue) => {
    const saleId = Number(saleIdValue || 0);
    if (!saleId) {
      setFormData((prev) => ({
        ...prev,
        sale_id: '',
        sale_code: '',
        quotation_code: '',
      }));
      resetSaleLines();
      return;
    }
    const option = saleOptions.find((row) => String(row.value) === String(saleId));
    setFormData((prev) => ({
      ...prev,
      sale_id: String(saleId),
      sale_code: option?.sale_code || '',
      quotation_code: option?.quotation_code || '',
      justification:
        prev.justification && String(prev.justification).trim() !== 'Upselling ·'
          ? prev.justification
          : `Upselling · Venta ${option?.sale_code || saleId}`,
    }));
    setSaleLinesLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/quotations/sales/${saleId}/detail`, {
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const detail = await response.json();
      const saleCode = String(detail?.sale?.sale_code || option?.sale_code || '').trim();
      const quotationCode = String(
        detail?.quotation?.quotation_code ||
          detail?.sale?.quotation_code ||
          option?.quotation_code ||
          ''
      ).trim();
      const upselling = upsellingLinesFromSaleDetail(detail, paintSkuSet, upsellingSkuSet);
      setFormData((prev) => ({
        ...prev,
        sale_id: String(saleId),
        sale_code: saleCode,
        quotation_code: quotationCode,
      }));
      setSaleLineItems(upselling);
      setSelectionMap(buildDefaultLineSelections(upselling));
    } catch (err) {
      console.error(err);
      setSaleLineItems([]);
      setSelectionMap({});
      setFormError(err?.message || 'No se pudieron cargar los ítems de la venta.');
    } finally {
      setSaleLinesLoading(false);
    }
  };

  const handleSelectionUpdate = (itemId, field, value) => {
    setSelectionMap((prev) => updateLineSelectionMap(prev, itemId, field, value));
  };

  const mapItemToForm = (item) => ({
    id: item.id,
    item_code: item.item_code || '',
    description: item.description || '',
    quantity: item.quantity ?? 1,
    unit: item.unit || 'UN',
    estimated_unit_price: item.estimated_unit_price ?? 0,
    unit_budget: item.unit_budget ?? item.estimated_unit_price ?? 0,
    purchase_motive: String(item.purchase_motive || 'stock').trim() || 'stock',
    sale_id: item.sale_id ? String(item.sale_id) : '',
    buyer_name: item.buyer_name || '',
    reference_link: item.reference_link || '',
  });

  const listTabs = useMemo(
    () => [
      { key: 'requests', label: 'Solicitudes', count: requests.length },
      { key: 'items', label: 'Ítems', count: itemRows.length },
    ],
    [requests.length, itemRows.length]
  );

  const listSubtitle = loading
    ? 'Cargando…'
    : listTab === 'items'
      ? itemsError || `${itemRows.length} ítems · adquisiciones upselling`
      : error || `${requests.length} solicitud${requests.length === 1 ? '' : 'es'} · adquisiciones upselling`;

  const openCreate = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormError('');
    resetSaleLines();
    setFormData({
      ...EMPTY_FORM,
      request_date: todayIso(),
      requester_name: String(user?.full_name || '').trim(),
      items: [emptyUpsellingLine()],
    });
    setShowForm(true);
  };

  const openDetail = async (row) => {
    if (!row?.id) return;
    setDetailLoading(true);
    try {
      const full = await purchaseRequestsAPI.getById(row.id);
      setDetailRequest(full);
    } catch (err) {
      console.error(err);
      setDetailRequest(row);
    } finally {
      setDetailLoading(false);
    }
  };

  const openEdit = async (request) => {
    const source = request?.items ? request : await purchaseRequestsAPI.getById(request.id);
    setDetailRequest(null);
    setIsEditing(true);
    setEditingId(source.id);
    setFormError('');
    resetSaleLines();
    setFormData({
      request_code: source.request_code || '',
      requester_name: source.requester_name || '',
      department: source.department || 'Comercial',
      supplier_id: source.supplier_id ? String(source.supplier_id) : '',
      request_date: source.request_date ? String(source.request_date).slice(0, 10) : todayIso(),
      needed_by_date: source.needed_by_date ? String(source.needed_by_date).slice(0, 10) : '',
      priority: source.priority || 'Normal',
      status: source.status || 'Enviada',
      justification: source.justification || 'Upselling · ',
      notes: source.notes || '',
      sale_id: '',
      sale_code: '',
      quotation_code: '',
      items:
        Array.isArray(source.items) && source.items.length
          ? source.items.map(mapItemToForm)
          : [emptyUpsellingLine()],
    });
    setShowForm(true);
  };

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const requester = String(formData.requester_name || '').trim();
    if (!requester) {
      setFormError('El solicitante es obligatorio.');
      return;
    }

    let items = [];
    const saleId = Number(formData.sale_id || 0) || null;
    const saleCode = String(formData.sale_code || '').trim();

    if (!isEditing) {
      if (!saleId) {
        setFormError('Debes anexar un código de venta.');
        return;
      }
      const selectionError = validateLineSelections(saleLineItems, selectionMap, 'upselling');
      if (selectionError) {
        setFormError(selectionError);
        return;
      }
      items = selectedQuotationLines(saleLineItems, selectionMap).map((item, index) => ({
        line_order: (index + 1) * 10,
        item_code: String(item.product_sku || item.item_code || '').trim(),
        description: String(item.item_name || item.product_name || '').trim(),
        quantity: Number(item.requested_quantity || 0),
        unit: String(item.unit || 'UN').trim() || 'UN',
        estimated_unit_price: Number(item.unit_price || 0),
        unit_budget: Number(item.unit_price || 0),
        purchase_motive: 'sale',
        sale_id: saleId,
        buyer_name: '',
        reference_link: '',
      }));
    } else {
      items = (formData.items || [])
        .map((item) => ({
          id: item.id || null,
          item_code: String(item.item_code || '').trim(),
          description: String(item.description || '').trim(),
          quantity: Number(item.quantity || 0),
          unit: String(item.unit || 'UN').trim() || 'UN',
          estimated_unit_price: Number(item.estimated_unit_price || 0),
          unit_budget: Number(item.unit_budget || 0),
          purchase_motive: String(item.purchase_motive || 'stock').trim() || 'stock',
          sale_id: Number(item.sale_id || 0) || null,
          buyer_name: String(item.buyer_name || '').trim(),
          reference_link: String(item.reference_link || '').trim(),
        }))
        .filter((item) => item.description || item.item_code);
      if (!items.length) {
        setFormError('Agrega al menos un ítem con descripción.');
        return;
      }
      const invalidSaleItem = items.find(
        (item) => item.purchase_motive === 'sale' && !item.sale_id
      );
      if (invalidSaleItem) {
        setFormError('Selecciona la venta asociada en los ítems con motivo Venta.');
        return;
      }
    }

    let justification = String(formData.justification || '').trim();
    if (!/upselling/i.test(justification)) {
      justification = `Upselling · ${justification}`.trim();
    }
    if (!isEditing && saleCode && !justification.includes(saleCode)) {
      justification = `${justification} / venta ${saleCode}`.trim();
    }

    const deadlineRaw = String(formData.needed_by_date || '').trim();
    const neededByDate = deadlineRaw.slice(0, 10) || null;
    const neededByTime = deadlineRaw.includes('T')
      ? String(deadlineRaw.split('T')[1] || '').slice(0, 5)
      : '';
    let notes = String(formData.notes || '').trim() || 'Solicitud upselling desde área comercial.';
    if (neededByTime && !notes.includes(`Hora límite: ${neededByTime}`)) {
      notes = `${notes}\nHora límite: ${neededByTime}`.trim();
    }

    const payload = {
      requester_name: requester,
      department: 'Comercial',
      supplier_id: Number(formData.supplier_id || 0) || null,
      request_date: formData.request_date || todayIso(),
      needed_by_date: neededByDate,
      priority: String(formData.priority || 'Normal').trim() || 'Normal',
      status: String(formData.status || 'Enviada').trim() || 'Enviada',
      justification,
      notes,
      items,
    };

    setIsSaving(true);
    setFormError('');
    try {
      if (isEditing && editingId) {
        await purchaseRequestsAPI.update(editingId, payload);
      } else {
        await purchaseRequestsAPI.create(payload);
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ ...EMPTY_FORM });
      resetSaleLines();
      await refreshAll();
      notifyPurchaseRequestsChanged();
    } catch (err) {
      setFormError(err?.message || 'No se pudo guardar la solicitud.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBulkDelete = async (ids) => {
    if (!ids?.length) return false;
    const ok = window.confirm(
      ids.length === 1 ? '¿Eliminar esta solicitud?' : `¿Eliminar ${ids.length} solicitudes?`
    );
    if (!ok) return false;
    await purchaseRequestsAPI.bulkDelete(ids);
    await refreshAll();
    notifyPurchaseRequestsChanged();
    return true;
  };

  return (
    <>
      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: ui.page,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>Solicitudes de upselling</h3>
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>{listSubtitle}</p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 36,
              padding: '0 14px',
              borderRadius: 8,
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> Nueva solicitud
          </button>
        </div>

        <RequestListViewTabs
          tabs={listTabs}
          activeKey={listTab}
          onChange={setListTab}
          isDarkMode={isDarkMode}
          ariaLabel="Vistas de solicitudes upselling"
        />

        {listTab === 'items' ? (
          <UpsellingRequestItemsTable
            rows={itemRows}
            loading={itemsLoading || detailLoading}
            error={itemsError}
            isDarkMode={isDarkMode}
            hideChrome
            onRequestClick={openDetail}
          />
        ) : (
          <PurchaseRequestsTable
            rows={requests}
            loading={loading || detailLoading}
            error={error}
            isDarkMode={isDarkMode}
            hideChrome
            attached
            onRowClick={openDetail}
            onBulkDelete={handleBulkDelete}
          />
        )}
      </div>

      <PurchaseRequestsForm
        isOpen={showForm}
        onClose={() => {
          if (isSaving) return;
          setShowForm(false);
          resetSaleLines();
        }}
        onSave={handleSave}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
        isEditing={isEditing}
        formData={formData}
        onChange={handleChange}
        formError={formError}
        supplierOptions={supplierOptions}
        areaOptions={areaOptions}
        requesterOptions={requesterOptions}
        buyerOptions={requesterOptions}
        saleOptions={saleOptions}
        productOptions={productOptions}
        formSubtitle="Comercial · Upselling"
        saleLineMode
        saleLineItems={saleLineItems}
        selectionMap={selectionMap}
        onSelectionUpdate={handleSelectionUpdate}
        saleLinesLoading={saleLinesLoading}
        onSaleChange={handleSaleChange}
      />

      <PurchaseRequestDetailModal
        isOpen={Boolean(detailRequest)}
        onClose={() => setDetailRequest(null)}
        purchaseRequest={detailRequest}
        isDarkMode={isDarkMode}
        onEdit={openEdit}
      />
    </>
  );
};

export default UpsellingRequestsPanel;
